import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axiosInstace";
import { toast } from "react-toastify";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import "./HiringFlow.scss";
import LockedButton from "../../components/LockedButton";
import { RECRUITMENT } from "../../config/moduleAccess";

// ─── Constants ────────────────────────────────────────────────────────────────
const API = `${process.env.REACT_APP_API_HR_URL}/hiring-flow`;
const UPDATE_API = `${process.env.REACT_APP_API_HR_URL}/hiring-flow/update/hiringflow/interviewround`;

const INTERVIEW_MODES = [
  ["ONLINE", "Online"],
  ["OFFLINE", "Offline"],
  ["HYBRID", "Hybrid"],
];

const MODE_META = {
  Online: { color: "#2563eb", bg: "#eff6ff", label: "Online" },
  Offline: { color: "#0f6e56", bg: "#e1f5ee", label: "Offline" },
  Hybrid: { color: "#854f0b", bg: "#faeeda", label: "Hybrid" },
};

const STATUS_META = {
  OPEN: { color: "#15803d", bg: "#dcfce7", label: "Open" },
  CLOSED: { color: "#b91c1c", bg: "#fee2e2", label: "Closed" },
  DRAFT: { color: "#6b7280", bg: "#f3f4f6", label: "Draft" },
  ON_HOLD: { color: "#92400e", bg: "#fef3c7", label: "On Hold" },
};

// ─── Small helpers ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || {
    color: "#6b7280",
    bg: "#f3f4f6",
    label: status,
  };
  return (
    <span className="hfd-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

const ModeBadge = ({ mode }) => {
  const m = MODE_META[mode] || { color: "#6b7280", bg: "#f3f4f6", label: mode };
  return (
    <span
      className="hfd-badge hfd-badge--sm"
      style={{ color: m.color, background: m.bg }}
    >
      {m.label}
    </span>
  );
};

const StatCard = ({ label, value, sub, accent }) => (
  <div className="hfd-stat" style={{ "--accent": accent }}>
    <div className="hfd-stat__bar" />
    <div className="hfd-stat__body">
      <div className="hfd-stat__text">
        <p className="hfd-stat__label">{label}</p>
        {sub && <p className="hfd-stat__sub">{sub}</p>}
      </div>
      <p className="hfd-stat__value">{value}</p>
    </div>
  </div>
);

// ─── Mini donut chart (pure SVG) ───────────────────────────────────────────────
const DonutChart = ({ segments, size = 72 }) => {
  const r = (size - 12) / 2;
  const cx = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let cumulative = 0;
  const slices = segments
    .filter((s) => s.value > 0)
    .map((seg) => {
      const pct = seg.value / total;
      const start = cumulative;
      cumulative += pct;
      const startAngle = start * 2 * Math.PI - Math.PI / 2;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cx + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cx + r * Math.sin(endAngle);
      const large = pct > 0.5 ? 1 : 0;
      return {
        ...seg,
        d: `M ${cx} ${cx} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      };
    });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} opacity="0.9" />
      ))}
      <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
      <text
        x={cx}
        y={cx + 4}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#1e293b"
      >
        {total}
      </text>
    </svg>
  );
};

// ─── Round Pipeline Timeline ────────────────────────────────────────────────────
const RoundTimeline = ({ rounds }) => {
  const sorted = [...rounds].sort((a, b) => a.sequence - b.sequence);
  return (
    <div className="hfd-timeline">
      {sorted.map((r, idx) => (
        <React.Fragment key={r.interviewRoundId}>
          <div
            className={`hfd-timeline__node ${r.initial ? "hfd-timeline__node--init" : r.final ? "hfd-timeline__node--fin" : ""}`}
          >
            <div className="hfd-timeline__circle">
              <span>{r.sequence}</span>
            </div>
            <div className="hfd-timeline__label">
              <p className="hfd-timeline__name">{r.roundName}</p>
              <div className="hfd-timeline__tags">
                <ModeBadge mode={r.interviewMode} />
                {r.initial && (
                  <span className="hfd-tag hfd-tag--init">Initial</span>
                )}
                {r.final && <span className="hfd-tag hfd-tag--fin">Final</span>}
              </div>
              {r.employees?.length > 0 && (
                <div className="hfd-timeline__panellists">
                  {r.employees.map((emp, index) => (
                    <span
                      key={emp.interviewerId}
                      className="hfd-panellist-chip"
                    >
                      {emp.interviewerName}
                      {index !== r.employees.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {idx < sorted.length - 1 && (
            <div className="hfd-timeline__connector" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Employee multi-select dropdown ────────────────────────────────────────────
const EmployeeSelect = ({
  idx,
  employeeIds,
  employees,
  empLoading,
  onToggle,
  openIdx,
  onOpen,
}) => {
  const selected = employees.filter((e) =>
    (employeeIds || []).includes(e.empId),
  );

  return (
    <div className="hfd-emp-wrap">
      <button
        type="button"
        className="hfd-emp-trigger"
        onClick={() => !empLoading && onOpen(openIdx === idx ? null : idx)}
        disabled={empLoading}
      >
        <span className="hfd-emp-trigger__chips">
          {selected.length === 0 ? (
            <span className="hfd-emp-trigger__placeholder">
              {empLoading ? "Loading…" : "Select interviewers"}
            </span>
          ) : (
            selected.map((emp) => (
              <span key={emp.empId} className="hfd-emp-chip">
                {emp.firstName} {emp.lastName}
                <span
                  role="button"
                  className="hfd-emp-chip__remove"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onToggle(idx, emp.empId);
                  }}
                >
                  ×
                </span>
              </span>
            ))
          )}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transform: openIdx === idx ? "rotate(180deg)" : "none",
            transition: "transform .15s",
            flexShrink: 0,
          }}
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="#6b7280"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {openIdx === idx && (
        <div className="hfd-emp-dropdown">
          {employees.length === 0 ? (
            <div className="hfd-emp-dropdown__empty">No employees found</div>
          ) : (
            employees.map((emp) => {
              const checked = (employeeIds || []).includes(emp.empId);
              return (
                <label
                  key={emp.empId}
                  className={`hfd-emp-option ${checked ? "hfd-emp-option--checked" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(idx, emp.empId)}
                    style={{ accentColor: "#2563eb" }}
                  />
                  <span className="hfd-emp-option__name">
                    {emp.firstName} {emp.lastName}
                    <span className="hfd-emp-option__email">
                      {emp.companyEmail}
                    </span>
                  </span>
                  {checked && (
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      style={{ marginLeft: "auto" }}
                    >
                      <path
                        d="M2.5 6.5l3 3 5-5"
                        stroke="#2563eb"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ─── Interview Rounds Builder ───────────────────────────────────────────────────
const InterviewRoundsBuilder = ({ rounds, onChange, errors, isEdit }) => {
  const [employees, setEmployees] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [openEmpDropdown, setOpenEmpDropdown] = useState(null);

  useEffect(() => {
    setEmpLoading(true);
    axios
      .get(`${process.env.REACT_APP_COMMON_API_URL}/employees/list`)
      .then((res) => setEmployees(res.data.data || []))
      .catch(console.error)
      .finally(() => setEmpLoading(false));
  }, []);

  useEffect(() => {
    if (openEmpDropdown === null) return;
    const handler = (e) => {
      if (!e.target.closest(".hfd-emp-wrap")) setOpenEmpDropdown(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openEmpDropdown]);

  const updateField = (idx, field, value) =>
    onChange(rounds.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const toggleEmployee = (idx, empId) => {
    const cur = rounds[idx].employeeIds || [];
    updateField(
      idx,
      "employeeIds",
      cur.includes(empId) ? cur.filter((id) => id !== empId) : [...cur, empId],
    );
  };

  const toggleFlag = (idx, flag) => {
    const turning = !rounds[idx][flag];
    onChange(
      rounds.map((r, i) => {
        if (i === idx) return { ...r, [flag]: turning };
        if (turning) return { ...r, [flag]: false };
        return r;
      }),
    );
  };

  const removeRound = (idx) =>
    onChange(
      rounds
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, sequence: i + 1 })),
    );

  return (
    <div className="hfd-rounds-builder">
      {rounds.map((r, idx) => (
        <div key={r._key ?? idx} className="hfd-round-card">
          <div className="hfd-round-card__header">
            <div className="hfd-round-card__seq">
              <span>{r.sequence}</span>
            </div>
            <span className="hfd-round-card__title">Round {r.sequence}</span>
            <div className="hfd-round-card__flags">
              {!isEdit && (
                <>
                  <button
                    type="button"
                    className={`hfd-flag-btn ${r.initial ? "hfd-flag-btn--init" : ""}`}
                    onClick={() => toggleFlag(idx, "initial")}
                  >
                    Initial
                  </button>
                  <button
                    type="button"
                    className={`hfd-flag-btn ${r.final ? "hfd-flag-btn--fin" : ""}`}
                    onClick={() => toggleFlag(idx, "final")}
                  >
                    Final
                  </button>
                </>
              )}
            </div>
            {!isEdit && rounds.length > 1 && (
              <button
                type="button"
                className="hfd-round-card__remove"
                onClick={() => removeRound(idx)}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M2.5 3.5h9M5.5 3.5V3a1 1 0 012 0v.5M4 3.5v7a1 1 0 001 1h4a1 1 0 001-1v-7"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="hfd-round-card__body">
            <div className="hfd-field-row">
              <div
                className={`hfd-field ${errors?.[`round_${r._key ?? idx}`] ? "hfd-field--err" : ""}`}
              >
                <label className="hfd-field__label">
                  Round name <span className="hfd-field__req">*</span>
                </label>
                <input
                  className="hfd-input"
                  value={r.roundName}
                  placeholder="e.g. Interview Round, HR Round"
                  onChange={(e) =>
                    updateField(idx, "roundName", e.target.value)
                  }
                />
                {errors?.[`round_${r._key ?? idx}`] && (
                  <span className="hfd-field__err">
                    {errors[`round_${r._key ?? idx}`]}
                  </span>
                )}
              </div>

              <div className="hfd-field">
                <label className="hfd-field__label">Interview mode</label>
                <select
                  className="hfd-input hfd-input--select"
                  value={r.interviewMode || "ONLINE"}
                  onChange={(e) =>
                    updateField(idx, "interviewMode", e.target.value)
                  }
                >
                  {INTERVIEW_MODES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="hfd-field">
              <label className="hfd-field__label">Interviewers</label>
              <EmployeeSelect
                idx={idx}
                employeeIds={r.employeeIds}
                employees={employees}
                empLoading={empLoading}
                onToggle={toggleEmployee}
                openIdx={openEmpDropdown}
                onOpen={setOpenEmpDropdown}
              />
            </div>

            <div className="hfd-field">
              <label className="hfd-field__label">Description</label>
              <textarea
                className="hfd-input hfd-input--textarea"
                value={r.description || ""}
                placeholder="Optional notes about this round…"
                rows={2}
                onChange={(e) =>
                  updateField(idx, "description", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Drawer ────────────────────────────────────────────────────────────────────
const HiringFlowDrawer = ({ flow, allFlows = [], onClose, onSaved }) => {
  const isEdit = !!flow;
  const [flowMode, setFlowMode] = useState("new");
  const [selectedFlowId, setSelectedFlowId] = useState("");
  const [flowName, setFlowName] = useState(flow?.hiringFlowName || "");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const makeDefaultRound = (seq, init, fin) => ({
    id: null,
    roundName: "",
    sequence: seq,
    interviewMode: "ONLINE",
    employeeIds: [],
    initial: init,
    final: fin,
    description: "",
    _key: Date.now() + seq,
  });

  const [rounds, setRounds] = useState(() =>
    flow?.rounds
      ? [...flow.rounds]
          .sort((a, b) => a.sequence - b.sequence)
          .map((r) => ({
            id: r.interviewRoundId ?? r.id ?? null,
            roundName: r.roundName || "",
            sequence: r.sequence,
            interviewMode: (r.interviewMode || "ONLINE").toUpperCase(),
            employeeIds:
              r.employeeIds || r.employees?.map((e) => e.interviewerId) || [],

            initial: r.initial || false,
            final: r.final || false,
            description: r.description || "",
            _key: r.interviewRoundId || Date.now() + Math.random(),
          }))
      : [makeDefaultRound(1, true, false), makeDefaultRound(2, false, true)],
  );

  const handleExistingSelect = (e) => {
    const id = e.target.value;
    setSelectedFlowId(id);
    setErrors((p) => ({ ...p, selectedFlowId: "" }));
    if (!id) return;
    const picked = allFlows.find((f) => String(f.hiringFlowId) === String(id));
    if (!picked) return;
    setFlowName(picked.hiringFlowName ? `${picked.hiringFlowName} (Copy)` : "");
    if (picked.rounds?.length) {
      setRounds(
        [...picked.rounds]
          .sort((a, b) => a.sequence - b.sequence)
          .map((r) => ({
            id: null,
            roundName: r.roundName || "",
            sequence: r.sequence,
            interviewMode: (r.interviewMode || "ONLINE").toUpperCase(),
            employeeIds:
              r.employeeIds || r.employees?.map((e) => e.interviewerId) || [],
            initial: r.initial || false,
            final: r.final || false,
            description: r.description || "",
            _key: r.interviewRoundId || Date.now() + Math.random(),
          })),
      );
    }
  };

  const addRound = () =>
    setRounds((prev) => [
      ...prev,
      makeDefaultRound(prev.length + 1, false, false),
    ]);

  const validate = () => {
    const e = {};
    if (!isEdit && flowMode === "existing") {
      if (!selectedFlowId) e.selectedFlowId = "Please select a hiring flow";
    } else {
      if (!flowName.trim()) e.flowName = "Flow name is required";
      rounds.forEach((r) => {
        if (!r.roundName.trim()) e[`round_${r._key}`] = "Round name required";
      });
      const initCount = rounds.filter((r) => r.initial).length;
      const finalCount = rounds.filter((r) => r.final).length;
      if (initCount !== 1)
        e._rounds = "Exactly one round must be marked as Initial";
      else if (finalCount !== 1)
        e._rounds = "Exactly one round must be marked as Final";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (!isEdit && flowMode === "existing") {
        const picked = allFlows.find(
          (f) => String(f.hiringFlowId) === String(selectedFlowId),
        );
        await axios.post(API, {
          hiringFlowName: flowName.trim() || picked?.hiringFlowName || "",
          rounds: (picked?.rounds || [])
            .sort((a, b) => a.sequence - b.sequence)
            .map((r) => ({
              roundName: r.roundName,
              sequence: r.sequence,
              interviewMode: (r.interviewMode || "ONLINE").toUpperCase(),
              employeeIds: r.employeeIds || [],
              initial: r.initial,
              final: r.final,
              description: r.description || "",
            })),
        });
        toast.success("Hiring flow created from existing!");
      } else if (isEdit) {
        await axios.put(UPDATE_API, {
          id: flow.hiringFlowId,
          hiringFlowName: flowName.trim(),
          interviewRoundsUpdateRequestDTOList: rounds.map((r) => ({
            ...(r.id ? { id: r.id } : {}),
            roundName: r.roundName.trim(),
            description: r.description || "",
            interviewMode: r.interviewMode.toUpperCase() || "ONLINE",
            employeeIds: r.employeeIds || [],
          })),
        });
        toast.success("Hiring flow updated!");
      } else {
        await axios.post(API, {
          hiringFlowName: flowName.trim(),
          rounds: rounds.map((r) => ({
            roundName: r.roundName.trim(),
            sequence: r.sequence,
            interviewMode: r.interviewMode.toUpperCase() || "ONLINE",
            employeeIds: r.employeeIds || [],
            initial: r.initial,
            final: r.final,
            description: r.description || "",
          })),
        });
        toast.success("Hiring flow created!");
      }
      onSaved();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to save hiring flow.",
      );
    } finally {
      setLoading(false);
    }
  };

  const initCount = rounds.filter((r) => r.initial).length;
  const finalCount = rounds.filter((r) => r.final).length;
  const previewFlow = allFlows.find(
    (f) => String(f.hiringFlowId) === String(selectedFlowId),
  );

  return (
    <>
      <div className="hfd-backdrop" onClick={onClose} />
      <div className="hfd-drawer">
        {/* Header */}
        <div className="hfd-drawer__header">
          <div className="hfd-drawer__header-left">
            <div className="hfd-drawer__icon-wrap">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle
                  cx="9"
                  cy="9"
                  r="7.5"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 9h6M9 6v6"
                  stroke="#2563eb"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div>
              <h2 className="hfd-drawer__title">
                {isEdit ? "Edit hiring flow" : "New hiring flow"}
              </h2>
              <p className="hfd-drawer__sub">
                {isEdit
                  ? `Editing: ${flow.hiringFlowName}`
                  : "Configure interview rounds and panellists"}
              </p>
            </div>
          </div>
          <div className="hfd-drawer__header-right">
            {!isEdit && (
              <div className="hfd-mode-toggle">
                <button
                  type="button"
                  className={`hfd-mode-toggle__btn ${flowMode === "existing" ? "active" : ""}`}
                  onClick={() => setFlowMode("existing")}
                >
                  Use existing
                </button>
                <button
                  type="button"
                  className={`hfd-mode-toggle__btn ${flowMode === "new" ? "active" : ""}`}
                  onClick={() => setFlowMode("new")}
                >
                  Create new
                </button>
              </div>
            )}
            <button
              className="hfd-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path
                  d="M4.5 4.5l9 9M13.5 4.5l-9 9"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="hfd-drawer__body">
          {/* Flow Name */}
          <div
            className={`hfd-field ${errors.flowName ? "hfd-field--err" : ""}`}
          >
            <label className="hfd-field__label">
              Flow name <span className="hfd-field__req">*</span>
            </label>
            <input
              className="hfd-input"
              value={flowName}
              placeholder="e.g. Standard Hiring, Sales Hiring Flow"
              onChange={(e) => {
                setFlowName(e.target.value);
                setErrors((p) => ({ ...p, flowName: "" }));
              }}
            />
            {errors.flowName && (
              <span className="hfd-field__err">{errors.flowName}</span>
            )}
          </div>

          {/* ══ USE EXISTING MODE ══ */}
          {!isEdit && flowMode === "existing" && (
            <div className="hfd-section">
              <p className="hfd-section__label">Select existing flow</p>
              <p className="hfd-section__hint">
                A new flow will be created based on the one you pick.
              </p>

              <div
                className={`hfd-field ${errors.selectedFlowId ? "hfd-field--err" : ""}`}
              >
                <label className="hfd-field__label">
                  Hiring flow <span className="hfd-field__req">*</span>
                </label>
                <select
                  className={`hfd-input hfd-input--select ${errors.selectedFlowId ? "hfd-input--err" : ""}`}
                  value={selectedFlowId}
                  onChange={handleExistingSelect}
                >
                  <option value="">Choose a hiring flow…</option>
                  {allFlows.map((f) => (
                    <option key={f.hiringFlowId} value={f.hiringFlowId}>
                      {f.hiringFlowName} ({f.rounds?.length || 0} rounds)
                    </option>
                  ))}
                </select>
                {errors.selectedFlowId && (
                  <span className="hfd-field__err">
                    {errors.selectedFlowId}
                  </span>
                )}
              </div>

              {previewFlow && (
                <div className="hfd-flow-preview">
                  <p className="hfd-flow-preview__label">
                    Preview — {previewFlow.hiringFlowName}
                  </p>
                  <div className="hfd-flow-preview__track">
                    {[...previewFlow.rounds]
                      .sort((a, b) => a.sequence - b.sequence)
                      .map((r, idx, arr) => (
                        <React.Fragment key={r.interviewRoundId ?? idx}>
                          <div
                            className={`hfd-flow-preview__dot ${r.initial ? "init" : r.final ? "fin" : ""}`}
                          >
                            {r.sequence}
                            <span className="hfd-flow-preview__name">
                              {r.roundName}
                            </span>
                          </div>
                          {idx < arr.length - 1 && (
                            <div className="hfd-flow-preview__line" />
                          )}
                        </React.Fragment>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ CREATE / EDIT ROUNDS MODE ══ */}
          {(isEdit || flowMode === "new") && (
            <div className="hfd-section">
              <div className="hfd-section__header">
                <p className="hfd-section__label">Interview rounds</p>
                <div className="hfd-legend">
                  <span className="hfd-legend__item hfd-legend__item--init">
                    Initial
                  </span>
                  <span className="hfd-legend__item hfd-legend__item--fin">
                    Final
                  </span>
                </div>
                {!isEdit && (
                  <button
                    type="button"
                    className="hfd-add-round-btn"
                    onClick={addRound}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                    Add round
                  </button>
                )}
              </div>

              {errors._rounds && (
                <div className="hfd-rounds-error">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle
                      cx="7"
                      cy="7"
                      r="6"
                      stroke="#dc2626"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M7 4v3.5M7 9.5v.5"
                      stroke="#dc2626"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  {errors._rounds}
                </div>
              )}

              <InterviewRoundsBuilder
                rounds={rounds}
                onChange={setRounds}
                errors={errors}
                isEdit={isEdit}
              />

              {/* Validation bar */}
              <div className="hfd-validation-bar">
                <span
                  className={`hfd-val-item ${initCount === 1 ? "ok" : "err"}`}
                >
                  {initCount === 1 ? "✓" : "○"} 1 Initial
                </span>
                <span
                  className={`hfd-val-item ${finalCount === 1 ? "ok" : "err"}`}
                >
                  {finalCount === 1 ? "✓" : "○"} 1 Final
                </span>
                <span className="hfd-val-item ok">
                  ✓ {rounds.length} total round{rounds.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="hfd-drawer__footer">
          <button className="hfd-btn hfd-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="hfd-btn hfd-btn--primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <span className="hfd-btn__spinner" />
            ) : isEdit ? (
              "Save changes"
            ) : flowMode === "existing" ? (
              "Create from existing"
            ) : (
              "Create flow"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Main Dashboard Page ────────────────────────────────────────────────────────
const AllHiringFlows = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [forceDeleteTarget, setForceDeleteTarget] = useState(null);
  const [forceDeleteMsg, setForceDeleteMsg] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState("ALL");

  const fetchFlows = useCallback(() => {
    setLoading(true);
    axios
      .get(API)
      .then((res) => setFlows(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // ── Computed stats ──
  const totalRounds = flows.reduce((s, f) => s + (f.rounds?.length || 0), 0);
  const totalJobPosts = flows.reduce(
    (s, f) => s + (f.jobPosts?.length || 0),
    0,
  );
  const avgRounds = flows.length
    ? (totalRounds / flows.length).toFixed(1)
    : "0";
  const flowsWithJobs = flows.filter((f) => f.jobPosts?.length > 0).length;

  const modeCount = flows.reduce((acc, f) => {
    f.rounds?.forEach((r) => {
      const mode = r.interviewMode;
      acc[mode] = (acc[mode] || 0) + 1;
    });
    return acc;
  }, {});

  // ── Filter / search ──
  const filteredFlows = flows.filter((f) => {
    const matchSearch = f.hiringFlowName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchMode =
      filterMode === "ALL"
        ? true
        : f.rounds?.some((r) => r.interviewMode === filterMode);
    return matchSearch && matchMode;
  });

  // ── Delete handlers ──
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/${deleteTarget.hiringFlowId}`);
      toast.success("Hiring flow deleted.");
      setDeleteTarget(null);
      fetchFlows();
    } catch (err) {
      const msg = err?.response?.data?.message || "";

      if (err?.response?.status === 409) {
        setForceDeleteMsg(msg);
        setForceDeleteTarget(deleteTarget);
      } else {
        toast.error(msg || "Failed to delete hiring flow.");
      }

      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleForceDeleteConfirm = async () => {
    if (!forceDeleteTarget) return;

    setDeleteLoading(true);

    try {
      await axios.delete(`${API}/force/${forceDeleteTarget.hiringFlowId}`);

      toast.success("Hiring flow force deleted.");
      setForceDeleteTarget(null);
      fetchFlows();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Force delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setDrawerOpen(true);
  };
  const openEdit = (f) => {
    setEditTarget(f);
    setDrawerOpen(true);
  };
  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="hfd-page">
      {/* ══ Page header ══ */}
      <div className="hfd-page__header">
        <div className="hfd-page__header-left">
          <div className="hfd-page__title-wrap">
            <h1 className="hfd-page__title">Hiring Flows</h1>
            <span className="hfd-page__count">{flows.length} flows</span>
          </div>
          <p className="hfd-page__sub">
            Define and manage interview pipelines for your job posts
          </p>
        </div>
        <LockedButton
          codes={[RECRUITMENT.HIRING_FLOW_CREATE]}
          className="hfd-create-btn"
          onClick={openCreate}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M7 1v12M1 7h12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          New Hiring Flow
        </LockedButton>
      </div>

      {/* ══ Stats row ══ */}
      <div className="hfd-stats-row">
        <StatCard
          label="Total flows"
          value={flows.length}
          sub={`${flowsWithJobs} with active job posts`}
          accent="#2563eb"
        />
        <StatCard
          label="Interview rounds"
          value={totalRounds}
          sub={`Avg ${avgRounds} rounds per flow`}
          accent="#0f6e56"
        />
        <StatCard
          label="Linked job posts"
          value={totalJobPosts}
          sub={`Across ${flowsWithJobs} flows`}
          accent="#854f0b"
        />
        <StatCard
          label="Mode breakdown"
          value={`${modeCount.Online || 0} / ${modeCount.Offline || 0} / ${modeCount.Hybrid || 0}`}
          sub="Online / Offline / Hybrid"
          accent="#6d28d9"
        />
      </div>

      {/* ══ Toolbar ══ */}
      <div className="hfd-toolbar">
        <div className="hfd-filter-tabs">
          {["ALL", "Online", "Offline", "Hybrid"].map((m) => (
            <button
              key={m}
              className={`hfd-filter-tab ${filterMode === m ? "active" : ""}`}
              onClick={() => setFilterMode(m)}
            >
              {m === "ALL"
                ? "All modes"
                : m.charAt(0) + m.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        <div className="hfd-search-wrap">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="hfd-search-icon"
          >
            <circle cx="6" cy="6" r="4.5" stroke="#9ca3af" strokeWidth="1.3" />
            <path
              d="M9.5 9.5l2.5 2.5"
              stroke="#9ca3af"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          <input
            className="hfd-search"
            placeholder="Search flows…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="hfd-search-clear"
              onClick={() => setSearchQuery("")}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* ══ Main content ══ */}
      {loading ? (
        <div className="hfd-loading">
          <div className="hfd-loading__spinner" />
          <p>Loading hiring flows…</p>
        </div>
      ) : flows.length === 0 ? (
        <div className="hfd-empty">
          <div className="hfd-empty__icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#e5e7eb" strokeWidth="2" />
              <path
                d="M16 24h16M24 16v16"
                stroke="#d1d5db"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h3>No hiring flows yet</h3>
          <p>
            Create a hiring flow to define interview stages for your job posts.
          </p>
          <LockedButton
            codes={[RECRUITMENT.HIRING_FLOW_CREATE]}
            className="hfd-btn hfd-btn--primary"
            onClick={openCreate}
          >
            Create your first flow
          </LockedButton>
        </div>
      ) : filteredFlows.length === 0 ? (
        <div className="hfd-empty hfd-empty--search">
          <p>
            No flows match your search.{" "}
            <button
              className="hfd-link"
              onClick={() => {
                setSearchQuery("");
                setFilterMode("ALL");
              }}
            >
              Clear filters
            </button>
          </p>
        </div>
      ) : (
        <div className="hfd-flow-grid">
          {/* ── Left: flow cards ── */}
          <div className="hfd-flow-list">
            {filteredFlows.map((f) => {
              const isExp = expandedId === f.hiringFlowId;
              const jobPosts = f.jobPosts || [];
              const sorted = [...(f.rounds || [])].sort(
                (a, b) => a.sequence - b.sequence,
              );
              const initR = sorted.find((r) => r.initial);
              const finalR = sorted.find((r) => r.final);

              return (
                <div
                  key={f.hiringFlowId}
                  className={`hfd-card ${isExp ? "hfd-card--exp" : ""}`}
                >
                  {/* Card top row */}
                  <div
                    className="hfd-card__top"
                    onClick={() => toggleExpand(f.hiringFlowId)}
                  >
                    <div className="hfd-card__left">
                      <div className="hfd-card__avatar">
                        {f.hiringFlowName.charAt(0).toUpperCase()}
                      </div>
                      <div className="hfd-card__info">
                        <h3 className="hfd-card__name">{f.hiringFlowName}</h3>
                        <div className="hfd-card__meta">
                          <span className="hfd-meta-chip">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                            >
                              <circle
                                cx="5"
                                cy="5"
                                r="4"
                                stroke="currentColor"
                                strokeWidth="1.2"
                              />
                              <path
                                d="M5 3v2l1.5 1.5"
                                stroke="currentColor"
                                strokeWidth="1.2"
                                strokeLinecap="round"
                              />
                            </svg>
                            {f.rounds?.length || 0} rounds
                          </span>
                          {initR && (
                            <span className="hfd-meta-chip hfd-meta-chip--init">
                              Starts: {initR.roundName}
                            </span>
                          )}
                          {finalR && (
                            <span className="hfd-meta-chip hfd-meta-chip--fin">
                              Ends: {finalR.roundName}
                            </span>
                          )}
                          {jobPosts.length > 0 && (
                            <span className="hfd-meta-chip hfd-meta-chip--jobs">
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 10 10"
                                fill="none"
                              >
                                <rect
                                  x="1"
                                  y="3"
                                  width="8"
                                  height="6"
                                  rx="1"
                                  stroke="currentColor"
                                  strokeWidth="1.1"
                                />
                                <path
                                  d="M3.5 3V2.5a1.5 1.5 0 013 0V3"
                                  stroke="currentColor"
                                  strokeWidth="1.1"
                                  strokeLinecap="round"
                                />
                              </svg>
                              {jobPosts.length} post
                              {jobPosts.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="hfd-card__right">
                      {/* Round dot trail */}
                      <div className="hfd-dot-trail">
                        {sorted.slice(0, 6).map((r) => (
                          <span
                            key={r.interviewRoundId}
                            className={`hfd-dot ${r.initial ? "hfd-dot--init" : r.final ? "hfd-dot--fin" : ""}`}
                            title={r.roundName}
                          />
                        ))}
                        {sorted.length > 6 && (
                          <span className="hfd-dot-more">
                            +{sorted.length - 6}
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div
                        className="hfd-card__actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="hfd-icon-btn hfd-icon-btn--edit"
                          onClick={() => openEdit(f)}
                          title="Edit flow"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M9.5 2.5l2 2L4 13H2v-2L9.5 2.5z"
                              stroke="currentColor"
                              strokeWidth="1.3"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          className="hfd-icon-btn hfd-icon-btn--delete"
                          onClick={() => setDeleteTarget(f)}
                          title="Delete flow"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M2.5 3.5h9M5.5 3.5V3a1 1 0 012 0v.5M4 3.5v7a1 1 0 001 1h4a1 1 0 001-1v-7"
                              stroke="currentColor"
                              strokeWidth="1.3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Chevron */}
                      <svg
                        className={`hfd-chevron ${isExp ? "hfd-chevron--open" : ""}`}
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 6l4 4 4-4"
                          stroke="#9ca3af"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {isExp && (
                    <div className="hfd-card__body">
                      {/* Round timeline */}
                      <div className="hfd-card__section">
                        <p className="hfd-card__section-label">
                          Interview pipeline
                        </p>
                        <RoundTimeline rounds={f.rounds || []} />
                      </div>

                      {/* Job posts */}
                      <div className="hfd-card__section">
                        <p className="hfd-card__section-label">
                          Linked job posts
                          {jobPosts.length > 0 && (
                            <span className="hfd-card__section-count">
                              {jobPosts.length}
                            </span>
                          )}
                        </p>
                        {jobPosts.length === 0 ? (
                          <p className="hfd-card__empty">
                            No job posts linked yet.
                          </p>
                        ) : (
                          <div className="hfd-jp-grid">
                            {jobPosts.map((jp) => (
                              <div key={jp.jobPostId} className="hfd-jp-card">
                                <div className="hfd-jp-card__left">
                                  <p className="hfd-jp-card__title">
                                    {jp.jobTitle}
                                  </p>
                                </div>
                                <StatusBadge status={jp.status} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Right: analytics sidebar ── */}
          <aside className="hfd-sidebar">
            {/* Mode distribution donut */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Mode distribution</p>
              <div className="hfd-donut-wrap">
                <DonutChart
                  size={84}
                  segments={[
                    { value: modeCount.Online || 0, color: "#2563eb" },
                    { value: modeCount.Offline || 0, color: "#0f6e56" },
                    { value: modeCount.Hybrid || 0, color: "#f59e0b" },
                  ]}
                />
                <div className="hfd-donut-legend">
                  {[
                    {
                      label: "Online",
                      val: modeCount.Online || 0,
                      color: "#2563eb",
                    },
                    {
                      label: "Offline",
                      val: modeCount.Offline || 0,
                      color: "#0f6e56",
                    },
                    {
                      label: "Hybrid",
                      val: modeCount.Hybrid || 0,
                      color: "#f59e0b",
                    },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="hfd-donut-legend__row">
                      <span
                        className="hfd-donut-legend__dot"
                        style={{ background: color }}
                      />
                      <span className="hfd-donut-legend__label">{label}</span>
                      <span className="hfd-donut-legend__val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Rounds per flow sparkbars */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Rounds per flow</p>
              <div className="hfd-sparkbars">
                {flows.map((f) => {
                  const count = f.rounds?.length || 0;
                  const max = Math.max(
                    ...flows.map((fl) => fl.rounds?.length || 0),
                    1,
                  );
                  const pct = (count / max) * 100;
                  return (
                    <div key={f.hiringFlowId} className="hfd-spark-row">
                      <span
                        className="hfd-spark-row__label"
                        title={f.hiringFlowName}
                      >
                        {f.hiringFlowName.length > 18
                          ? f.hiringFlowName.slice(0, 17) + "…"
                          : f.hiringFlowName}
                      </span>
                      <div className="hfd-spark-row__track">
                        <div
                          className="hfd-spark-row__fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="hfd-spark-row__val">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Job post coverage */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Job post coverage</p>
              <div className="hfd-sparkbars">
                {flows.map((f) => {
                  const count = f.jobPosts?.length || 0;
                  const max = Math.max(
                    ...flows.map((fl) => fl.jobPosts?.length || 0),
                    1,
                  );
                  const pct = (count / max) * 100;
                  return (
                    <div key={f.hiringFlowId} className="hfd-spark-row">
                      <span
                        className="hfd-spark-row__label"
                        title={f.hiringFlowName}
                      >
                        {f.hiringFlowName.length > 18
                          ? f.hiringFlowName.slice(0, 17) + "…"
                          : f.hiringFlowName}
                      </span>
                      <div className="hfd-spark-row__track">
                        <div
                          className="hfd-spark-row__fill hfd-spark-row__fill--amber"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="hfd-spark-row__val">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick tips */}
            <div className="hfd-widget hfd-widget--tips">
              <p className="hfd-widget__title">Quick tips</p>
              <ul className="hfd-tips">
                <li>
                  Mark exactly <strong>one</strong> round as Initial and one as
                  Final.
                </li>
                <li>
                  Use <strong>Hybrid</strong> mode for rounds that can be remote
                  or in-person.
                </li>
                <li>Flows linked to job posts can only be force-deleted.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}

      {/* ── Drawer ── */}
      {drawerOpen && (
        <HiringFlowDrawer
          flow={editTarget}
          allFlows={flows}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchFlows();
          }}
        />
      )}

      {/* ── Delete popups ── */}
      <WarningPopup
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDecline={() => setDeleteTarget(null)}
        onAccept={handleDeleteConfirm}
        title="Delete Hiring Flow"
        message={`Are you sure you want to delete "${deleteTarget?.hiringFlowName}"? This action cannot be undone.`}
        acceptText={deleteLoading ? "Deleting…" : "Delete"}
        declineText="Cancel"
      />

      <WarningPopup
        isOpen={!!forceDeleteTarget}
        onClose={() => {
          setForceDeleteTarget(null);
          setForceDeleteMsg("");
        }}
        onDecline={() => {
          setForceDeleteTarget(null);
          setForceDeleteMsg("");
        }}
        onAccept={handleForceDeleteConfirm}
        title="⚠️ Force Delete Hiring Flow"
        message={
          forceDeleteMsg ||
          `"${forceDeleteTarget?.hiringFlowName}" is linked to job posts. Force deleting will remove all mappings permanently. Continue?`
        }
        acceptText={deleteLoading ? "Force deleting…" : "Force Delete"}
        declineText="Cancel"
      />
    </div>
  );
};

export default AllHiringFlows;
